import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import { useEffect, useMemo, useState } from 'react'
import { Box, Text } from '@chakra-ui/react'

export interface MapEntity {
  name: string
  place: string
  type: 'sib' | 'bank'
}

interface GeoPoint {
  lat: number
  lng: number
}

// Pre-populated cache for all known mock data places — avoids API calls for current dataset
const geocodeCache = new Map<string, GeoPoint | null>([
  ['60311 Frankfurt', { lat: 50.1109, lng: 8.6821 }],
  ['68159 Mannheim', { lat: 49.4891, lng: 8.4669 }],
  ['01067 Dresden', { lat: 51.0504, lng: 13.7373 }],
  ['50259 Pulheim', { lat: 50.9978, lng: 6.8003 }],
  ['70173 Stuttgart', { lat: 48.7758, lng: 9.1829 }],
  ['04109 Leipzig', { lat: 51.3397, lng: 12.3731 }],
  ['50667 Köln', { lat: 50.9333, lng: 6.95 }],
  ['80331 München', { lat: 48.1374, lng: 11.5754 }],
  ['30159 Hannover', { lat: 52.3759, lng: 9.732 }],
  ['90402 Nürnberg', { lat: 49.4521, lng: 11.0767 }],
  ['21244 Buchholz', { lat: 53.3333, lng: 9.8667 }],
  ['55116 Mainz', { lat: 49.9928, lng: 8.2473 }],
  ['37073 Göttingen', { lat: 51.5338, lng: 9.9333 }],
  ['40213 Düsseldorf', { lat: 51.2217, lng: 6.7762 }],
  ['83646 Bad Tölz', { lat: 47.7608, lng: 11.5584 }],
  ['20095 Hamburg', { lat: 53.5753, lng: 10.0153 }],
])

// Rate-limited Nominatim fallback for places not in the cache
let lastNominatimRequest = 0
async function geocodeViaApi(place: string): Promise<GeoPoint | null> {
  const wait = Math.max(0, 1200 - (Date.now() - lastNominatimRequest))
  if (wait > 0) await new Promise(r => setTimeout(r, wait))
  lastNominatimRequest = Date.now()
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(place)}&countrycodes=de,at&limit=1`,
      { headers: { 'User-Agent': 'sib-frontend/1.0 (internal)' } }
    )
    const data = await res.json()
    return data[0] ? { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) } : null
  } catch {
    return null
  }
}

async function geocodePlace(place: string): Promise<GeoPoint | null> {
  if (!place.trim()) return null
  if (geocodeCache.has(place)) return geocodeCache.get(place)!
  const point = await geocodeViaApi(place)
  geocodeCache.set(place, point)
  return point
}

function createCircleIcon(fill: string, stroke: string, size: number): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${fill};border:2.5px solid ${stroke};box-shadow:0 1px 5px rgba(0,0,0,0.35);box-sizing:border-box"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2) - 3],
  })
}

const ICONS = {
  sibFocus: createCircleIcon('#2563EB', '#1E40AF', 26),
  sibOther: createCircleIcon('#93C5FD', '#3B82F6', 16),
  bankFocus: createCircleIcon('#7C3AED', '#5B21B6', 26),
  bankOther: createCircleIcon('#C4B5FD', '#8B5CF6', 16),
}

function BoundsUpdater({ points }: { points: GeoPoint[] }) {
  const map = useMap()
  useEffect(() => {
    if (points.length === 0) return
    if (points.length === 1) {
      map.setView([points[0]!.lat, points[0]!.lng], 11)
      return
    }
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng] as [number, number]))
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 12 })
  }, [map, points])
  return null
}

interface Props {
  focus: MapEntity
  others: MapEntity[]
}

export function CapacityAreaMap({ focus, others }: Props) {
  const [resolvedFocus, setResolvedFocus] = useState<GeoPoint | null>(null)
  const [resolvedOthers, setResolvedOthers] = useState<(GeoPoint | null)[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setResolvedFocus(null)
    setResolvedOthers([])

    async function run() {
      const fp = await geocodePlace(focus.place)
      if (cancelled) return
      setResolvedFocus(fp)

      const results: (GeoPoint | null)[] = []
      for (const entity of others) {
        if (cancelled) return
        results.push(await geocodePlace(entity.place))
      }

      if (!cancelled) {
        setResolvedOthers(results)
        setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [focus.name, focus.place, others])

  const allPoints = useMemo<GeoPoint[]>(() => {
    const pts: GeoPoint[] = []
    if (resolvedFocus) pts.push(resolvedFocus)
    resolvedOthers.forEach(p => {
      if (p) pts.push(p)
    })
    return pts
  }, [resolvedFocus, resolvedOthers])

  if (loading && !resolvedFocus) {
    return (
      <Box h="420px" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="sm" color="fg.subtle">
          Standorte werden geladen…
        </Text>
      </Box>
    )
  }

  return (
    <Box position="relative" h="420px">
      {loading && (
        <Box
          position="absolute"
          top={2}
          right={2}
          zIndex={1000}
          bg="bg"
          px={2}
          py={1}
          borderRadius="sm"
          borderWidth="1px"
          borderColor="border"
          pointerEvents="none"
        >
          <Text fontSize="xs" color="fg.subtle">
            Weitere Standorte werden geladen…
          </Text>
        </Box>
      )}

      {/* Legend */}
      <Box
        position="absolute"
        bottom={6}
        left={2}
        zIndex={1000}
        bg="bg"
        px={2}
        py={1.5}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border"
        pointerEvents="none"
        display="flex"
        flexDir="column"
        gap={1}
      >
        {focus.type === 'sib' ? (
          <>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                w="10px"
                h="10px"
                borderRadius="full"
                bg="#2563EB"
                border="2px solid #1E40AF"
                flexShrink={0}
              />
              <Text fontSize="xs" color="fg.subtle">
                SIB (Fokus)
              </Text>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                w="10px"
                h="10px"
                borderRadius="full"
                bg="#C4B5FD"
                border="2px solid #8B5CF6"
                flexShrink={0}
              />
              <Text fontSize="xs" color="fg.subtle">
                Bank
              </Text>
            </Box>
          </>
        ) : (
          <>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                w="10px"
                h="10px"
                borderRadius="full"
                bg="#7C3AED"
                border="2px solid #5B21B6"
                flexShrink={0}
              />
              <Text fontSize="xs" color="fg.subtle">
                Bank (Fokus)
              </Text>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                w="10px"
                h="10px"
                borderRadius="full"
                bg="#93C5FD"
                border="2px solid #3B82F6"
                flexShrink={0}
              />
              <Text fontSize="xs" color="fg.subtle">
                SIB
              </Text>
            </Box>
          </>
        )}
      </Box>

      <MapContainer
        center={[51.1657, 10.4515]}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        zoomControl
      >
        <TileLayer
          url={`https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png?key=${
            import.meta.env.VITE_CARTO_API_KEY
          }`}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          subdomains="abcd"
          maxZoom={19}
        />

        <BoundsUpdater points={allPoints} />

        {resolvedFocus && (
          <Marker
            position={[resolvedFocus.lat, resolvedFocus.lng]}
            icon={focus.type === 'sib' ? ICONS.sibFocus : ICONS.bankFocus}
            zIndexOffset={1000}
          >
            <Popup>
              <strong>{focus.name}</strong>
              <br />
              {focus.place}
            </Popup>
          </Marker>
        )}

        {others.map((entity, i) => {
          const pt = resolvedOthers[i]
          if (!pt) return null
          return (
            <Marker
              key={`${entity.type}-${entity.name}`}
              position={[pt.lat, pt.lng]}
              icon={entity.type === 'sib' ? ICONS.sibOther : ICONS.bankOther}
            >
              <Popup>
                <strong>{entity.name}</strong>
                <br />
                {entity.place}
              </Popup>
            </Marker>
          )
        })}
      </MapContainer>
    </Box>
  )
}
